/**
 * RS / Designer Index — slow orbit of designer orbs, one or two wheels.
 *
 * Orb positions are pure CSS: each orb carries an `--orb-angle` and each
 * wheel exposes `--rs-spin`, advanced here on a rAF loop (wheels alternate
 * direction). The loop runs only while the section is in the viewport and
 * pauses while any orb is hovered or focused, or while the brief is open.
 *
 * Brief mode: orbs are buttons; clicking one fills and opens the mini brief
 * (name, meta, description, image, Explore more). Esc / click outside / ×
 * close it and focus returns to the orb that opened it.
 */
const SPIN_DEG_PER_SEC = 6;

class RsDesignerIndex extends HTMLElement {
  #raf = null;
  #lastTime = 0;
  #observer = null;
  #spin = 0;
  #paused = false;
  #pausedHover = false;
  #briefOpen = false;

  connectedCallback() {
    this.wheels = Array.from(this.querySelectorAll('[data-rs-wheel]'));
    if (this.wheels.length === 0) return;

    for (const wheel of this.wheels) {
      for (const orb of wheel.querySelectorAll('[data-rs-orb]')) {
        // Pause only from a real mouse: touch taps synthesize mouseenter
        // without ever leaving, which would freeze the wheel on mobile
        orb.addEventListener('pointerenter', (e) => {
          if (e.pointerType === 'mouse') this.#pausedHover = true;
        });
        orb.addEventListener('pointerleave', () => (this.#pausedHover = false));
      }
      wheel.addEventListener('focusin', () => (this.#paused = true));
      wheel.addEventListener('focusout', () => (this.#paused = false));
    }

    this.#setupBrief();

    this.#observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          this.#start();
        } else {
          this.#stop();
        }
      }
    });
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#stop();
    this.#observer?.disconnect();
    if (this.#briefOpen) this.#closeBrief();
  }

  #setupBrief() {
    this.briefPanel = this.querySelector('[data-rs-brief-panel]');
    if (!this.briefPanel) return;

    this.briefName = this.briefPanel.querySelector('[data-rs-brief-name]');
    this.briefMeta = this.briefPanel.querySelector('[data-rs-brief-meta]');
    this.briefDesc = this.briefPanel.querySelector('[data-rs-brief-desc]');
    this.briefLink = this.briefPanel.querySelector('[data-rs-brief-link]');
    this.briefImage = this.briefPanel.querySelector('[data-rs-brief-image]');
    this.briefClose = this.briefPanel.querySelector('[data-rs-brief-close]');

    for (const orb of this.querySelectorAll('button[data-rs-orb]')) {
      orb.addEventListener('click', () => this.#openBrief(orb));
    }
    this.briefClose?.addEventListener('click', () => this.#closeBrief());

    this.onDocClick = (event) => {
      if (!this.#briefOpen) return;
      if (this.briefPanel.querySelector('.rs-index__brief-box').contains(event.target)) return;
      if (event.target.closest('[data-rs-orb]')) return;
      this.#closeBrief();
    };
    this.onKeydown = (event) => {
      if (!this.#briefOpen || event.key !== 'Escape') return;
      event.preventDefault();
      this.#closeBrief();
    };
  }

  #openBrief(orb) {
    this.invokerOrb = orb;
    if (this.briefName) this.briefName.textContent = orb.dataset.name || '';
    if (this.briefMeta) {
      this.briefMeta.textContent = orb.dataset.meta || '';
      this.briefMeta.hidden = !orb.dataset.meta;
    }
    if (this.briefDesc) {
      this.briefDesc.textContent = orb.dataset.desc || '';
      this.briefDesc.hidden = !orb.dataset.desc;
    }
    if (this.briefLink) {
      if (orb.dataset.url) {
        this.briefLink.href = orb.dataset.url;
        this.briefLink.hidden = false;
      } else {
        this.briefLink.hidden = true;
      }
    }
    if (this.briefImage) {
      if (orb.dataset.image) {
        this.briefImage.src = orb.dataset.image;
        this.briefImage.alt = orb.dataset.imageAlt || orb.dataset.name || '';
        this.briefImage.hidden = false;
      } else {
        this.briefImage.hidden = true;
      }
    }

    this.briefPanel.hidden = false;
    this.#briefOpen = true;
    this.#paused = true;
    document.addEventListener('click', this.onDocClick);
    document.addEventListener('keydown', this.onKeydown);
    this.briefClose?.focus();
  }

  #closeBrief() {
    this.briefPanel.hidden = true;
    this.#briefOpen = false;
    this.#paused = false;
    document.removeEventListener('click', this.onDocClick);
    document.removeEventListener('keydown', this.onKeydown);
    this.invokerOrb?.focus();
  }

  #start() {
    if (this.#raf) return;
    this.#lastTime = performance.now();
    this.#raf = requestAnimationFrame(this.#tick);
  }

  #stop() {
    cancelAnimationFrame(this.#raf);
    this.#raf = null;
  }

  #tick = (time) => {
    const dt = Math.min((time - this.#lastTime) / 1000, 0.05);
    this.#lastTime = time;

    if (!this.#paused && !this.#pausedHover) {
      this.#spin = (this.#spin + SPIN_DEG_PER_SEC * dt) % 360;
      for (let i = 0; i < this.wheels.length; i++) {
        const dir = i % 2 === 0 ? 1 : -1;
        this.wheels[i].style.setProperty('--rs-spin', `${this.#spin * dir}deg`);
      }
    }

    this.#raf = requestAnimationFrame(this.#tick);
  };
}

if (!customElements.get('rs-designer-index')) {
  customElements.define('rs-designer-index', RsDesignerIndex);
}
